import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowLeft, Banknote, CreditCard, Send, Check, Download, Camera, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { CouponQr } from "./WalkinCredits";
import { downloadCouponPdf } from "./CouponReprint";
import { usePosCharge, posFee } from "./usePosCharge";

/**
 * Accountant "Sell New Coupon".
 *
 * Now always invoked from within an event drill-in — the parent
 * (EventDetailDashboard) passes `eventId`/`eventName`/`eventStart`/`eventEnd`,
 * so the accountant only picks amount + email + payment type + optional days.
 */
export default function SellCoupon({ onBack, eventId, eventName, eventStart, eventEnd }) {
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [selectedDates, setSelectedDates] = useState([]);
  const charges = usePosCharge();
  const [submitting, setSubmitting] = useState(false);
  const [issued, setIssued] = useState(null);
  const { authAxios } = useAuth();


  // Derive selectable days from the event window passed in as props.
  const eventDays = (() => {
    if (!eventStart || !eventEnd) return [];
    const start = new Date(eventStart);
    const end = new Date(eventEnd);
    const days = [];
    const d = new Date(start);
    while (d <= end) {
      days.push(d.toISOString().slice(0, 10));
      d.setDate(d.getDate() + 1);
    }
    return days;
  })();

  const toggleDay = (day) => {
    setSelectedDates((prev) => (prev.includes(day) ? prev.filter((x) => x !== day) : [...prev, day]));
  };

  const amountNum = parseFloat(amount) || 0;
  const chargeAmount = paymentType === "digital" ? posFee(charges, amountNum) : 0;
  const totalCollected = amountNum + chargeAmount;

  const handleSubmit = async () => {
    if (!eventId) return toast.error("Missing event context");
    if (!amountNum || amountNum <= 0) return toast.error("Enter a valid amount");
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return toast.error("Enter a valid email or leave it empty");
    setSubmitting(true);
    try {
      const res = await authAxios.post("/coupons", {
        event_id: eventId,
        amount: amountNum,
        payment_type: paymentType,
        email: email.trim().toLowerCase() || null,
        direct_purchase: true,
        valid_dates: selectedDates.length > 0 ? selectedDates : null,
      });
      setIssued(res.data);
      toast.success(email.trim() ? "Coupon issued and emailed to buyer" : "Coupon issued");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to issue coupon");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setIssued(null); setAmount(""); setEmail(""); setSelectedDates([]); setPaymentType("cash");
  };

  const downloadPdf = () => downloadCouponPdf(authAxios, issued.code);

  if (issued) {
    return (
      <div data-testid="sell-coupon-success">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-600 mb-4">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-4 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500 mx-auto flex items-center justify-center mb-3">
            <Check className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-green-800 mb-1">Coupon Issued</h2>
          <p className="text-sm text-green-600 mb-3 flex items-center justify-center gap-1" data-testid="issued-coupon-delivery">
            {issued.issued_to_email ? <><Mail className="w-4 h-4" /> PDF emailed to {issued.issued_to_email}</> : <><Camera className="w-4 h-4" /> Ask the buyer to photograph the QR or download the PDF</>}
          </p>
          <div className="flex justify-center mb-3">
            <CouponQr credit={{ coupon_code: issued.code, amount: issued.amount }} testid="issued-coupon-qr" />
          </div>
          <button onClick={downloadPdf} className="mb-4 px-4 py-2 rounded-full bg-white border text-sm font-semibold text-[#1e3a5f] inline-flex items-center gap-1.5" data-testid="issued-coupon-pdf-btn">
            <Download className="w-4 h-4" /> Download PDF
          </button>
          <div className="bg-white rounded-xl p-4 text-left space-y-1">
            <p className="text-xs text-slate-500">Code</p>
            <p className="font-mono text-2xl font-bold text-[#1e3a5f] mb-2" data-testid="issued-coupon-code">{issued.code}</p>
            <p className="text-xs text-slate-500">Face value</p>
            <p className="font-bold text-[#1e3a5f] mb-2">{"\u20ac"}{issued.amount.toFixed(2)}</p>
            {issued.tax_amount > 0 && (
              <>
                <p className="text-xs text-slate-500">Payment processing charges collected</p>
                <p className="font-bold text-amber-600 mb-2">{"\u20ac"}{issued.tax_amount.toFixed(2)}</p>
              </>
            )}
            <p className="text-xs text-slate-500">Payment</p>
            <p className="font-medium capitalize">{issued.payment_type}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={handleReset} className="flex-1 py-3 rounded-full bg-[#1e3a5f] text-white font-bold" data-testid="sell-another-btn">Sell Another</button>
          <button onClick={onBack} className="flex-1 py-3 rounded-full border-2 border-slate-300 text-slate-600 font-bold">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sell-coupon-form">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 mb-4">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>
      <h2 className="font-bold text-lg text-[#1e3a5f] mb-1">Sell New Coupon</h2>
      {eventName && <p className="text-slate-500 text-sm mb-1">{eventName}</p>}
      <p className="text-slate-400 text-xs mb-5">Direct spot-sale to a walk-in buyer.</p>

      {/* Amount */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-3">
        <label className="text-xs text-slate-500 mb-2 block">Coupon Amount (€)</label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">{"\u20ac"}</span>
          <input
            type="number" step="0.01" min="0" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full p-3 pl-8 rounded-xl border text-xl font-bold"
            placeholder="0.00"
            data-testid="sell-coupon-amount-input"
          />
        </div>
      </div>

      {/* Buyer email */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-3">
        <label className="text-xs text-slate-500 mb-2 block">Buyer Email (optional)</label>
        <input
          type="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-3 rounded-xl border"
          placeholder="buyer@example.com"
          data-testid="sell-coupon-email-input"
        />
        <p className="text-xs text-slate-400 mt-1">If given, the PDF coupon is emailed. Otherwise the buyer photographs the QR or you download the PDF.</p>
      </div>

      {/* Payment type */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-3">
        <label className="text-xs text-slate-500 mb-2 block">Payment Type</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: "cash", label: "Cash", icon: Banknote },
            { id: "digital", label: "Digital / Card", icon: CreditCard },
          ].map((pt) => (
            <button
              key={pt.id}
              onClick={() => setPaymentType(pt.id)}
              className={`py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors ${paymentType === pt.id ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              data-testid={`sell-coupon-payment-${pt.id}`}
            >
              <pt.icon className="w-4 h-4" />
              {pt.label}
            </button>
          ))}
        </div>
        {paymentType === "digital" && chargeAmount > 0 && (
          <div className="mt-3 bg-amber-50 rounded-xl p-3 text-sm">
            <p className="text-slate-700 mb-1">
              Payment processing charges: <strong className="text-amber-700">{"\u20ac"}{chargeAmount.toFixed(2)}</strong>
              {" "}({charges.type === "percent" ? `${charges.value}%` : `flat \u20ac${charges.value.toFixed(2)}`})
            </p>
            <p className="font-bold text-[#1e3a5f]" data-testid="sell-coupon-total-preview">
              Buyer pays: {"\u20ac"}{totalCollected.toFixed(2)}
            </p>
          </div>
        )}
      </div>

      {/* Valid dates */}
      {eventDays.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-3">
          <label className="text-xs text-slate-500 mb-2 block">Valid on days (leave empty = today only)</label>
          <div className="flex flex-wrap gap-2">
            {eventDays.map((day) => {
              const active = selectedDates.includes(day);
              return (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`px-3 py-2 rounded-full text-sm font-medium ${active ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-600"}`}
                  data-testid={`sell-coupon-day-${day}`}
                >
                  {new Date(day).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={submitting || !amountNum}
        className="w-full py-4 rounded-full bg-green-500 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="sell-coupon-submit-btn"
      >
        <Send className="w-5 h-5" />
        {submitting ? "Issuing..." : `Issue Coupon${amountNum > 0 ? ` \u2014 \u20ac${totalCollected.toFixed(2)}` : ""}`}
      </button>
    </div>
  );
}
